<script setup lang="ts">
import TempWidget from '../../components/temp/Temp.vue';
import HumidityWidget from '../../components/humidity/Humidity.vue';
import BarLevelWidget from './components/BarLevel.vue';
import SymbolStatusWidget from './components/SymbolStatus.vue';
import MotionWidget from './components/Motion.vue';
import DeviceMeta from '../DeviceMeta';
import { type MetaProps } from '../DeviceMeta/lib';
import { type DeviceValue } from '../../types/index';
import { type PropType, computed } from 'vue';

const props = defineProps({
  temp: {
    type: Object as PropType<DeviceValue>,
    required: true,
  },
  tvoc: {
    type: Object as PropType<DeviceValue>,
    required: true,
  },
  illumination: {
    type: Object as PropType<DeviceValue>,
    required: true,
  },
  humidity: {
    type: Object as PropType<DeviceValue>,
    required: true,
  },
  pir: {
    type: Object as PropType<DeviceValue>,
    default: undefined
  },
  co2: {
    type: Object as PropType<DeviceValue>,
    required: true,
  },
  timestamp: {
    type: Object as PropType<Date>,
    required: true,
  },
  meta: {
    type: Object as PropType<MetaProps>,
    default: undefined
  },
});

type MeasurementStatus = 'good' | 'polluted' | 'bad';

type Threshold = { low: number, high: number };

const thresholds: Record<'co2' | 'tvoc', Threshold> = {
  co2: { low: 1000, high: 1500 },
  tvoc: { low: 3, high: 4 }
};

const calculateIndoorStatus = (
  co2: number,
  tvoc: number
) => {
  const determineStatus = (value: number, threshold: 'co2' | 'tvoc'): MeasurementStatus =>
    value > thresholds[threshold].high
      ? 'bad'
      : value > thresholds[threshold].low
        ? 'polluted'
        : 'good';

  const co2Status = determineStatus(co2, 'co2');
  const tvocStatus = determineStatus(tvoc, 'tvoc');

  if (co2Status == 'bad' && tvocStatus == 'bad') return 'bad';

  if (co2Status == 'bad' || tvocStatus == 'bad') return 'polluted';

  if (co2Status == 'polluted' && tvocStatus == 'polluted') return 'warning';

  if (co2Status == 'polluted' || tvocStatus == 'polluted') return 'neutral';

  return 'excellent';
};

const temp = computed(() => props.temp.value);
const humidity = computed(() => props.humidity.value);
const co2 = computed(() => props.co2.value);
const tvoc = computed(() => props.tvoc.value);
const illumination = computed(() => props.illumination.value);
const pir = computed(() => props.pir?.value);

const timestamp = computed(() => new Date(props.timestamp).toLocaleString());

const symbolStatus = computed(() => calculateIndoorStatus(co2.value, tvoc.value));
</script>

<template>
  <div class="w-full h-full flex flex-col">
    <div class="w-full flex flex-1 flex-col justify-around">
      <div class="flex flex-row justify-center items-center w-full">
        <div class="flex justify-center w-1/3 h-20 border-r-2 border-border pt-4 px-4">
          <div class="flex-1 max-w-32 h-full">
            <BarLevelWidget
              :value="tvoc"
              :min="0"
              :max="5"
              label="TVOC"
            />
          </div>
        </div>

        <div class="flex w-1/3 h-20 border-r-2 justify-center border-border pt-4 px-4">
          <div class="flex-1 max-w-32 h-full">
            <BarLevelWidget
              :value="illumination"
              :min="0"
              :max="5"
              :label="$t('indoorClimate.illumination')"
            />
          </div>
        </div>

        <div class="w-1/3 h-20 px-4">
          <SymbolStatusWidget :status="symbolStatus" />
        </div>
      </div>

      <div class="border-b-2 border-border" />

      <div class="flex flex-row w-full">
        <div class="flex w-1/3 h-[72px] border-r-2 border-border items-center justify-center">
          <div class="h-9">
            <TempWidget :value="temp" />
          </div>
        </div>

        <div class="flex w-1/3 h-[72px] border-r-2 border-border items-center justify-center">
          <MotionWidget
            :value="!!pir"
            :timestamp="new Date(timestamp)"
          />
        </div>

        <div class="flex w-1/3 h-[72px] items-center justify-center">
          <div class="h-10">
            <HumidityWidget :value="humidity" />
          </div>
        </div>
      </div>

      <div class="flex flex-wrap text-onboard-shade2 text-xl text-foreground pt-4 justify-center">
        {{ co2 }}ppm
      </div>
    </div>
    <DeviceMeta
      v-if="props.meta"
      v-bind="props.meta"
    />
  </div>
</template>
